import { useState, useEffect } from 'react';
import { Trophy, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getThumbnailUrl } from '@/lib/cloudinary';
import type { Tournament } from '@shared/schema';

interface TournamentLogoProps {
  tournament?: Tournament | null;
  logoUrl?: string | null;
  tournamentName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallbackColor?: string;
  showName?: boolean;
  'data-testid'?: string;
}

const sizeConfig = {
  xs: {
    container: 'h-8 min-w-8',
    icon: 'w-4 h-4',
    text: 'text-xs',
    padding: 'p-1',
  },
  sm: {
    container: 'h-12 min-w-12',
    icon: 'w-6 h-6',
    text: 'text-sm',
    padding: 'p-1.5',
  },
  md: {
    container: 'h-16 min-w-16',
    icon: 'w-8 h-8',
    text: 'text-base',
    padding: 'p-2',
  },
  lg: {
    container: 'h-24 min-w-24',
    icon: 'w-12 h-12',
    text: 'text-lg',
    padding: 'p-3',
  },
  xl: {
    container: 'h-32 min-w-32',
    icon: 'w-16 h-16',
    text: 'text-xl',
    padding: 'p-4',
  },
};

const nameSizeConfig = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
};

export function TournamentLogo({
  tournament,
  logoUrl,
  tournamentName,
  size = 'md',
  className,
  fallbackColor,
  showName = false,
  'data-testid': dataTestId,
}: TournamentLogoProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);

  const url = logoUrl || tournament?.logoUrl;
  const name = tournamentName || tournament?.customName || tournament?.name || 'Tournament';
  const displayName = name.trim();
  const initial = displayName.charAt(0).toUpperCase();
  const config = sizeConfig[size];

  // Reset error state when URL changes
  useEffect(() => {
    if (url) {
      setImageError(false);
      setImageLoading(true);
      setLogoLoaded(false);
    }
  }, [url]);

  // Determine fallback color - prioritize tournament primary color, then provided fallback
  const getFallbackColor = () => {
    if (fallbackColor) return fallbackColor;
    if (tournament?.primaryColor) return tournament.primaryColor;
    // Default to Field Green from Dugout Pro theme
    return 'hsl(125, 35%, 45%)';
  };

  const backgroundColor = getFallbackColor();

  if (!url || imageError) {
    // Fallback: Show trophy icon in colored circle
    const logoElement = (
      <div
        className={cn(
          'rounded-lg flex items-center justify-center font-bold text-white shadow-sm flex-shrink-0',
          config.container,
          config.padding,
          className
        )}
        style={{ backgroundColor }}
        title={displayName}
        data-testid={dataTestId}
      >
        {imageLoading && !logoLoaded && url ? (
          <Loader2 className={cn('animate-spin text-white/70', config.icon)} />
        ) : (
          <Trophy className={cn('text-white', config.icon)} />
        )}
      </div>
    );

    if (showName) {
      return (
        <div className="flex items-center gap-3">
          {logoElement}
          <span className={cn('font-heading font-bold text-foreground', nameSizeConfig[size])}>
            {displayName}
          </span>
        </div>
      );
    }

    return logoElement;
  }

  const logoElement = (
    <div
      className={cn(
        'relative flex items-center justify-center flex-shrink-0 rounded-lg overflow-hidden bg-card',
        config.container,
        className
      )}
      data-testid={dataTestId}
    >
      {imageLoading && !logoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted animate-pulse">
          <Loader2 className={cn('animate-spin text-muted-foreground', config.icon)} />
        </div>
      )}
      <img
        src={getThumbnailUrl(url, 200, 200)}
        alt={`${displayName} logo`}
        className={cn(
          'max-w-full max-h-full object-contain',
          config.padding,
          imageLoading && !logoLoaded && 'opacity-0'
        )}
        onLoad={() => {
          setImageLoading(false);
          setLogoLoaded(true);
        }}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
      />
    </div>
  );

  if (showName) {
    return (
      <div className="flex items-center gap-3">
        {logoElement}
        <span className={cn('font-heading font-bold text-foreground', nameSizeConfig[size])}>
          {displayName}
        </span>
      </div>
    );
  }

  return logoElement;
}
